class AnnouncementStorage {
  static getItem(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  static setItem(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      // Storage can be unavailable in some privacy modes.
    }
  }

  static removeItem(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      // Storage can be unavailable in some privacy modes.
    }
  }
}

if (!customElements.get('announcement-wrapper-2')) {
  class AnnouncementWrapper2 extends HTMLElement {
    constructor() {
      super();

      this.closeTopBar = this.closeTopBar.bind(this);
    }

    connectedCallback() {
      this.topBar = this.querySelector('[data-announcement-top-bar]');
      this.bar = this.querySelector('[data-announcement-bar]');
      this.closeTopButton = this.querySelector('[data-announcement-close-top]');

      if (document.documentElement.classList.contains('top-bar-closed')) {
        this.classList.add('is-top-bar-closed');
      }

      this.closeTopButton?.addEventListener('click', this.closeTopBar);
    }

    disconnectedCallback() {
      this.closeTopButton?.removeEventListener('click', this.closeTopBar);
    }

    closeTopBar() {
      this.topBar.hidden = true;
      if (this.bar) {
        this.bar.hidden = false;
      }
      this.classList.add('is-top-bar-closed');
      document.documentElement.classList.add('top-bar-closed');
      AnnouncementStorage.setItem('topBarClosed', 'true');
    }
  }

  customElements.define('announcement-wrapper-2', AnnouncementWrapper2);
}

if (!customElements.get('announcement-bar-2')) {
  class AnnouncementBar2 extends HTMLElement {
    constructor() {
      super();

      this.items = Array.from(this.querySelectorAll('.announcement-2__item'));
      this.closeButton = this.querySelector('[data-announcement-close-bar]');
      this.mediaQuery = window.matchMedia('(max-width: 767px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.activeIndex = this.items.findIndex((item) => item.classList.contains('is-active'));
      this.isPaused = false;

      if (this.activeIndex < 0) {
        this.activeIndex = 0;
      }

      this.closeBar = this.closeBar.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
      this.pauseSlider = this.pauseSlider.bind(this);
      this.resumeSlider = this.resumeSlider.bind(this);
      this.showNextSlide = this.showNextSlide.bind(this);
    }

    connectedCallback() {
      this.closeButton?.addEventListener('click', this.closeBar);
      this.addEventListener('mouseenter', this.pauseSlider);
      this.addEventListener('mouseleave', this.resumeSlider);
      this.addMediaQueryListener(this.mediaQuery);
      this.addMediaQueryListener(this.reducedMotionQuery);

      this.updateSliderState();
    }

    disconnectedCallback() {
      this.closeButton?.removeEventListener('click', this.closeBar);
      this.removeEventListener('mouseenter', this.pauseSlider);
      this.removeEventListener('mouseleave', this.resumeSlider);
      this.removeMediaQueryListener(this.mediaQuery);
      this.removeMediaQueryListener(this.reducedMotionQuery);
      window.clearTimeout(this.slideTimer);
      window.clearTimeout(this.exitTimer);
    }

    closeBar() {
      document.documentElement.classList.add('announcement-bar-closed');
      AnnouncementStorage.setItem('announcementBarClosed', 'true');
      AnnouncementStorage.removeItem('BarClosed');
      this.hidden = true;
      window.clearTimeout(this.slideTimer);
      window.clearTimeout(this.exitTimer);
    }

    onMediaChange() {
      this.updateSliderState();
    }

    pauseSlider() {
      this.isPaused = true;
    }

    resumeSlider() {
      this.isPaused = false;
    }

    updateSliderState() {
      window.clearTimeout(this.slideTimer);
      window.clearTimeout(this.exitTimer);

      this.isSliderEnabled = this.mediaQuery.matches && this.items.length > 1;
      this.isMotionReduced = this.reducedMotionQuery.matches;
      this.classList.toggle('is-slider-enabled', this.isSliderEnabled);
      this.classList.toggle('is-reduced-motion', this.isMotionReduced);

      if (!this.isSliderEnabled) {
        this.items.forEach((item) => {
          item.classList.remove('is-active', 'is-exiting');
          item.removeAttribute('aria-hidden');
        });
        return;
      }

      this.setActiveSlide(this.activeIndex);

      if (!this.isMotionReduced) {
        this.queueNextSlide();
      }
    }

    queueNextSlide() {
      window.clearTimeout(this.slideTimer);
      this.slideTimer = window.setTimeout(this.showNextSlide, Number(this.dataset.slideDelay) || 5000);
    }

    showNextSlide() {
      if (!this.isSliderEnabled || this.isMotionReduced) return;

      if (this.isPaused || !this.isInViewport()) {
        this.queueNextSlide();
        return;
      }

      const previousIndex = this.activeIndex;
      const nextIndex = this.activeIndex + 1 < this.items.length ? this.activeIndex + 1 : 0;
      const previousItem = this.items[previousIndex];

      previousItem.classList.remove('is-active');
      previousItem.classList.add('is-exiting');
      this.setActiveSlide(nextIndex);

      window.clearTimeout(this.exitTimer);
      this.exitTimer = window.setTimeout(() => {
        previousItem.classList.remove('is-exiting');
      }, 180);

      this.queueNextSlide();
    }

    setActiveSlide(index) {
      this.activeIndex = index;

      this.items.forEach((item, itemIndex) => {
        const isActive = itemIndex === this.activeIndex;
        item.classList.toggle('is-active', isActive);
        if (isActive) {
          item.classList.remove('is-exiting');
        }
        item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
    }

    addMediaQueryListener(query) {
      if (query.addEventListener) {
        query.addEventListener('change', this.onMediaChange);
      } else {
        query.addListener(this.onMediaChange);
      }
    }

    removeMediaQueryListener(query) {
      if (query.removeEventListener) {
        query.removeEventListener('change', this.onMediaChange);
      } else {
        query.removeListener(this.onMediaChange);
      }
    }

    isInViewport() {
      if (window.theme?.elementUtil?.isInViewport) {
        return window.theme.elementUtil.isInViewport(this);
      }

      const rect = this.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight;
    }
  }

  customElements.define('announcement-bar-2', AnnouncementBar2);
}
